import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Moon, Gift, Heart, Utensils, BookOpen, Star, Users } from 'lucide-react';

export default function RatgeberRamadanGeschenke() {
  return (
    <main className="pt-20 md:pt-24 bg-[#FDFCF8]">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#4A5D4F]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link
            to="/ratgeber"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Alle Ratgeber
          </Link>
          <div className="flex items-center gap-2 text-[#D4C5A9] text-sm mb-4">
            <Calendar className="w-4 h-4" />
            <span>Aktualisiert: Februar 2026</span>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-white mb-6 leading-tight">
            Ramadan Geschenke: Die schönsten Ideen für den Fastenmonat
          </h1>
          <p className="text-lg text-white/80 leading-relaxed">
            Ein umfassender Guide zu bedeutungsvollen Geschenken während des Ramadan – 
            von Iftar-Körben bis zu spirituellen Präsenten.
          </p>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          
          {/* Introduction */}
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-[#2C332E] leading-relaxed mb-8">
              Der Ramadan ist der heiligste Monat im islamischen Kalender – eine Zeit des Fastens, 
              der Besinnung und der Großzügigkeit. Das Schenken während des Ramadan hat eine besondere 
              Bedeutung und stärkt die Bande zwischen Familie, Freunden und der Gemeinschaft. In diesem 
              Guide zeigen wir Ihnen die schönsten Ramadan Geschenkideen und erklären die Traditionen 
              hinter dem Schenken im Fastenmonat.
            </p>

            {/* Table of Contents */}
            <div className="bg-[#F5F5F0] rounded-2xl p-6 md:p-8 mb-12">
              <h2 className="font-serif text-xl font-medium text-[#2C332E] mb-4 flex items-center gap-2">
                <Moon className="w-5 h-5 text-[#4A5D4F]" />
                Inhaltsverzeichnis
              </h2>
              <ul className="space-y-2 text-[#4A5D4F]">
                <li><a href="#bedeutung" className="hover:underline">1. Die Bedeutung des Schenkens im Ramadan</a></li>
                <li><a href="#wann" className="hover:underline">2. Wann ist Ramadan 2026?</a></li>
                <li><a href="#iftar" className="hover:underline">3. Geschenke für das Iftar (Fastenbrechen)</a></li>
                <li><a href="#spirituell" className="hover:underline">4. Spirituelle Geschenkideen</a></li>
                <li><a href="#kinder" className="hover:underline">5. Ramadan Geschenke für Kinder</a></li>
                <li><a href="#gastgeber" className="hover:underline">6. Geschenke für Gastgeber</a></li>
                <li><a href="#nachbarn" className="hover:underline">7. Geschenke für nicht-muslimische Nachbarn</a></li>
                <li><a href="#selbstgemacht" className="hover:underline">8. DIY Ramadan Geschenkideen</a></li>
              </ul>
            </div>

            {/* Section 1 */}
            <section id="bedeutung" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  1. Die Bedeutung des Schenkens im Ramadan
                </h2>
              </div>
              
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Der Ramadan ist ein Monat der Barmherzigkeit und Großzügigkeit. Das Schenken während 
                dieser Zeit ist mehr als nur ein sozialer Brauch – es ist ein Ausdruck von <strong>Sadaqah</strong> 
                (freiwilliger Wohltätigkeit) und stärkt das Gemeinschaftsgefühl der <strong>Ummah</strong> (muslimische Gemeinschaft).
              </p>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Der Prophet Muhammad (Friede sei mit ihm) war bekannt für seine besondere Großzügigkeit 
                während des Ramadan. Ibn Abbas berichtete: "Der Gesandte Allahs war der großzügigste aller 
                Menschen, und am großzügigsten war er im Ramadan." Diese Tradition inspiriert Muslime 
                weltweit, während des Fastenmonats besonders freigiebig zu sein.
              </p>

              <div className="bg-[#F5F5F0] rounded-xl p-6 my-6">
                <h4 className="font-medium text-[#2C332E] mb-3">Warum ist Schenken im Ramadan besonders?</h4>
                <ul className="text-[#4B5563] space-y-2">
                  <li>• <strong>Verstärkte Belohnung:</strong> Gute Taten im Ramadan werden vervielfacht</li>
                  <li>• <strong>Gemeinschaft stärken:</strong> Das Teilen beim Iftar verbindet Menschen</li>
                  <li>• <strong>Bedürftige unterstützen:</strong> Zakat und Sadaqah sind im Ramadan besonders wichtig</li>
                  <li>• <strong>Dankbarkeit zeigen:</strong> Für die Segnungen, die wir haben</li>
                </ul>
              </div>
            </section>

            {/* Section 2 */}
            <section id="wann" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  2. Wann ist Ramadan 2026?
                </h2>
              </div>

              <div className="bg-[#4A5D4F] text-white rounded-xl p-6 mb-6">
                <p className="text-lg font-medium mb-2">Ramadan 2026</p>
                <p className="text-2xl font-serif mb-2">18. Februar – 19. März 2026</p>
                <p className="text-white/70 text-sm">
                  * Das genaue Datum hängt von der Mondsichtung ab und kann um 1-2 Tage variieren
                </p>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-4">
                Der Ramadan dauert 29 oder 30 Tage, abhängig vom Mondkalender. Geschenke können zu 
                verschiedenen Zeitpunkten überreicht werden:
              </p>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Zum Ramadan-Beginn:</strong> Als Willkommensgruß für den heiligen Monat</li>
                <li><strong>Während des Ramadan:</strong> Besonders zu Iftar-Einladungen</li>
                <li><strong>In den letzten 10 Nächten:</strong> Die spirituell intensivste Zeit</li>
                <li><strong>Zu Eid ul Fitr:</strong> Zum Abschluss des Fastenmonats</li>
              </ul>
            </section>

            {/* Section 3 - Iftar */}
            <section id="iftar" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <Utensils className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  3. Geschenke für das Iftar (Fastenbrechen)
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-4">
                Das Iftar ist das tägliche Fastenbrechen bei Sonnenuntergang – ein besonderer Moment, 
                der oft mit Familie und Freunden geteilt wird. Geschenke, die das Iftar bereichern, 
                sind besonders willkommen.
              </p>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Datteln – Das klassische Iftar-Geschenk</h3>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Der Prophet Muhammad (Friede sei mit ihm) brach sein Fasten traditionell mit Datteln. 
                Hochwertige Datteln sind daher das perfekte Ramadan-Geschenk:
              </p>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Medjool-Datteln:</strong> Groß, saftig und süß – der "König der Datteln"</li>
                <li><strong>Ajwa-Datteln:</strong> Aus Medina, mit besonderer religiöser Bedeutung</li>
                <li><strong>Deglet Nour:</strong> Mild-süß und vielseitig verwendbar</li>
                <li><strong>Dattel-Geschenkboxen:</strong> Elegant verpackte Sortimente</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Iftar-Körbe zusammenstellen</h3>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Ein liebevoll zusammengestellter Iftar-Korb ist ein großzügiges Geschenk:
              </p>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li>Premium-Datteln verschiedener Sorten</li>
                <li>Arabische Süßigkeiten (Baklava, Ma'amoul)</li>
                <li>Hochwertiger Tee oder Kaffee</li>
                <li>Orientalische Gewürze</li>
                <li>Trockenfrüchte und Nüsse</li>
                <li>Rosenwasser oder Orangenblütenwasser</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Getränke für das Iftar</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Hochwertiger Tee:</strong> Marokkanischer Minztee, Chai, Kräutertees</li>
                <li><strong>Arabischer Kaffee:</strong> Mit Kardamom verfeinert</li>
                <li><strong>Jallab-Sirup:</strong> Traditionelles Ramadan-Getränk</li>
                <li><strong>Tamar Hindi:</strong> Erfrischendes Tamarindengetränk</li>
              </ul>
            </section>

            {/* Section 4 - Spirituell */}
            <section id="spirituell" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  4. Spirituelle Geschenkideen
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-4">
                Der Ramadan ist eine Zeit der spirituellen Vertiefung. Geschenke, die diese Reise 
                unterstützen, sind besonders bedeutungsvoll:
              </p>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Quran und islamische Literatur</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Quran in schöner Ausgabe:</strong> Mit Übersetzung und Tafsir</li>
                <li><strong>Quran-Ständer (Rehal):</strong> Aus Holz oder verziert</li>
                <li><strong>Hadith-Sammlungen:</strong> Bukhari, Muslim oder thematische Auswahlen</li>
                <li><strong>Bücher über Ramadan:</strong> Spirituelle Begleiter für den Monat</li>
                <li><strong>Dua-Bücher:</strong> Sammlungen von Bittgebeten</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Gebetszubehör</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Hochwertiger Gebetsteppich:</strong> Weich, tragbar, mit Kompass</li>
                <li><strong>Tasbih (Gebetskette):</strong> 99 Perlen für Dhikr</li>
                <li><strong>Gebetskleidung:</strong> Abaya, Thobe oder Gebetsgewand</li>
                <li><strong>Digitaler Quran-Player:</strong> Für das Hören während der Hausarbeit</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Ramadan-Kalender</h3>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Ähnlich wie Adventskalender sind Ramadan-Kalender eine moderne Tradition geworden:
              </p>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li>30 kleine Türchen mit Überraschungen</li>
                <li>Tägliche Duas oder Hadithe zum Reflektieren</li>
                <li>Gefüllt mit Süßigkeiten, Schokolade oder kleinen Geschenken</li>
                <li>Besonders beliebt bei Kindern und Familien</li>
              </ul>
            </section>

            {/* Section 5 - Kinder */}
            <section id="kinder" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <Star className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  5. Ramadan Geschenke für Kinder
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-4">
                Der Ramadan ist eine wunderbare Gelegenheit, Kindern die Bedeutung des Fastenmonats 
                näherzubringen. Geschenke können dabei helfen, positive Erinnerungen zu schaffen:
              </p>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Bücher über Ramadan</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>"Mama, was ist Ramadan?":</strong> Erklärt den Fastenmonat kindgerecht</li>
                <li><strong>Ramadan-Geschichten:</strong> Mit bunten Illustrationen</li>
                <li><strong>Mein erstes Fasten:</strong> Für Kinder, die zum ersten Mal fasten</li>
                <li><strong>Islamische Gute-Nacht-Geschichten:</strong> Für die Zeit vor dem Schlafengehen</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Aktivitäten und Spiele</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Ramadan-Kalender für Kinder:</strong> Mit täglichen Überraschungen</li>
                <li><strong>Mond-Beobachtungs-Set:</strong> Für die Mondsichtung</li>
                <li><strong>Islamische Puzzle und Spiele:</strong> Spielend lernen</li>
                <li><strong>Bastelsets:</strong> Ramadan-Dekorationen selbst gestalten</li>
                <li><strong>Gutes-Taten-Tagebuch:</strong> Zum Festhalten von Sadaqah</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">"Erste Fasten"-Geschenke</h3>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Wenn ein Kind zum ersten Mal fastet (oft mit 7-10 Jahren), ist das ein besonderer 
                Meilenstein. Geschenke können diesen Moment würdigen:
              </p>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li>Personalisierter Gebetsteppich mit Namen</li>
                <li>Erste eigene Gebetskette</li>
                <li>Besonderes Iftar-Erlebnis oder Geschenkbox</li>
                <li>Zertifikat "Mein erstes Fasten"</li>
              </ul>
            </section>

            {/* Section 6 - Gastgeber */}
            <section id="gastgeber" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  6. Geschenke für Gastgeber
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-4">
                Werden Sie zum Iftar eingeladen? Als Gast ist es eine schöne Geste, dem Gastgeber 
                ein kleines Geschenk mitzubringen:
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <h4 className="font-medium text-[#2C332E] mb-2">🌹 Für das Zuhause</h4>
                  <ul className="text-sm text-[#4B5563] space-y-1">
                    <li>• Duftkerzen mit Oud oder Rose</li>
                    <li>• Räucherstäbchen oder Bakhoor</li>
                    <li>• Blumen oder Pflanzen</li>
                    <li>• Dekorative Laternen</li>
                  </ul>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <h4 className="font-medium text-[#2C332E] mb-2">🍽️ Für die Küche</h4>
                  <ul className="text-sm text-[#4B5563] space-y-1">
                    <li>• Premium-Datteln</li>
                    <li>• Orientalische Süßigkeiten</li>
                    <li>• Hochwertiger Tee</li>
                    <li>• Gewürz-Sets</li>
                  </ul>
                </div>
              </div>

              <p className="text-[#4B5563] leading-relaxed">
                <strong>Tipp:</strong> Es ist üblich, dass Gastgeber einen Teil des Essens mit Gästen 
                teilen. Bieten Sie an, ein Gericht zum Iftar beizusteuern – das ist eine sehr 
                geschätzte Geste!
              </p>
            </section>

            {/* Section 7 - Nachbarn */}
            <section id="nachbarn" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <Gift className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  7. Geschenke für nicht-muslimische Nachbarn
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-4">
                Der Ramadan ist eine wunderbare Gelegenheit, Brücken zu bauen und den interkulturellen 
                Dialog zu fördern. Ein kleines Geschenk an nicht-muslimische Nachbarn zeigt 
                Wertschätzung und öffnet Türen für Gespräche:
              </p>

              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Selbstgemachte Süßigkeiten:</strong> Baklava, Ma'amoul oder andere Leckereien</li>
                <li><strong>Dattel-Box mit Erklärung:</strong> Mit einer kleinen Karte über Ramadan</li>
                <li><strong>Einladung zum Iftar:</strong> Der persönlichste Weg, Ramadan zu teilen</li>
                <li><strong>"Ramadan Mubarak"-Karte:</strong> Mit guten Wünschen und kurzer Erklärung</li>
              </ul>

              <div className="bg-[#F5F5F0] rounded-xl p-6 my-6">
                <h4 className="font-medium text-[#2C332E] mb-3">💡 Beispieltext für eine Karte</h4>
                <p className="text-[#4B5563] italic">
                  "Liebe Nachbarn, wir feiern gerade den Ramadan – den muslimischen Fastenmonat. 
                  Wir möchten diese besondere Zeit mit Ihnen teilen und überreichen Ihnen eine 
                  kleine Aufmerksamkeit. Ramadan Mubarak (gesegneter Ramadan)!"
                </p>
              </div>
            </section>

            {/* Section 8 - DIY */}
            <section id="selbstgemacht" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                  <Star className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  8. DIY Ramadan Geschenkideen
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-4">
                Selbstgemachte Geschenke haben einen besonderen Wert – sie zeigen Zeit, Mühe und 
                persönliche Zuwendung. Hier sind einige Ideen:
              </p>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Kulinarische DIY-Geschenke</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Selbstgemachte Baklava:</strong> Mit Pistazien und Honig</li>
                <li><strong>Ma'amoul:</strong> Gefüllte Grießkekse</li>
                <li><strong>Dattel-Trüffel:</strong> Datteln mit Nüssen und Schokolade</li>
                <li><strong>Gewürzmischungen:</strong> Ras el Hanout, Baharat oder Chai-Gewürz</li>
                <li><strong>Eingelegte Zitronen:</strong> Marokkanische Spezialität</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Kreative Geschenke</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Handgeschriebene Dua-Karten:</strong> Personalisierte Bittgebete</li>
                <li><strong>Ramadan-Tagebuch:</strong> Selbstgebunden oder dekoriert</li>
                <li><strong>Bemalte Ramadan-Dekoration:</strong> Laternen, Sterne, Monde</li>
                <li><strong>Foto-Geschenk:</strong> Album mit Familienerinnerungen</li>
                <li><strong>Kalligraphie-Kunst:</strong> Verse oder Namen in arabischer Schrift</li>
              </ul>
            </section>

            {/* Conclusion */}
            <section className="mb-12">
              <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E] mb-6">
                Fazit: Schenken mit Herz im Ramadan
              </h2>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Das Schenken im Ramadan ist mehr als ein gesellschaftlicher Brauch – es ist ein 
                Ausdruck von Liebe, Großzügigkeit und Gemeinschaftssinn. Ob Sie Premium-Datteln 
                verschenken, einen Iftar-Korb zusammenstellen oder etwas Selbstgemachtes überreichen – 
                das Wichtigste ist die Intention und die Liebe, die Sie in Ihr Geschenk legen.
              </p>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Nutzen Sie diesen besonderen Monat, um Beziehungen zu stärken, Freude zu verbreiten 
                und den Geist der Großzügigkeit zu leben. Der Prophet Muhammad (Friede sei mit ihm) 
                sagte: "Die beste Tat ist es, Freude in das Herz eines Gläubigen zu bringen."
              </p>
              <p className="text-xl text-[#4A5D4F] font-medium">
                Ramadan Mubarak! 🌙
              </p>
            </section>

            {/* CTA */}
            <div className="bg-[#4A5D4F] rounded-2xl p-8 text-center">
              <h3 className="font-serif text-2xl font-medium text-white mb-4">
                Entdecken Sie unsere Ramadan-Geschenke
              </h3>
              <p className="text-white/80 mb-6">
                Handverlesene Geschenkideen für den Fastenmonat und Eid ul Fitr
              </p>
              <Link
                to="/eid-ul-fitr"
                className="inline-flex items-center gap-2 bg-white text-[#4A5D4F] px-8 py-3 rounded-full font-medium hover:bg-[#D4C5A9] transition-colors"
              >
                Jetzt entdecken
                <Moon className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </article>
    </main>
  );
}
